// src/lib/consent.ts
// GDPR cookie consent module. Client-only reads/writes; safe to import from server modules
// (guarded by `typeof window`/`document` checks).
//
// Cookie shape (JSON, URL-encoded):
//   { v: 1, ts: 1748400000, necessary: true, preferences: false, analytics: true, marketing: false }
//
// Bumping COOKIE_VERSION re-prompts every visitor (use when adding a new category or
// after a material policy change). Necessary cookies are always true and cannot be
// opted out of.

import { detectEmbedFraming, embedCookieAttributes } from "./embed/cookies";

export const COOKIE_NAME = "niftipay_consent";
export const COOKIE_VERSION = 1;
export const COOKIE_MAX_AGE_DAYS = 180;

export type ConsentCategory = "necessary" | "preferences" | "analytics" | "marketing";

export type ConsentState = {
  v: number;
  ts: number;
  necessary: true;
  preferences: boolean;
  analytics: boolean;
  marketing: boolean;
};

export type ConsentInput = Partial<Omit<ConsentState, "v" | "ts" | "necessary">>;

const ONE_DAY_SECONDS = 60 * 60 * 24;

function readRawCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${name}=`;
  const parts = document.cookie ? document.cookie.split("; ") : [];
  for (const part of parts) {
    if (part.startsWith(prefix)) {
      return decodeURIComponent(part.slice(prefix.length));
    }
  }
  return null;
}

function writeRawCookie(name: string, value: string, maxAgeSeconds: number): void {
  if (typeof document === "undefined") return;
  const https = typeof window !== "undefined" && window.location.protocol === "https:";
  const sameSite = embedCookieAttributes(detectEmbedFraming(), https);
  document.cookie = `${name}=${encodeURIComponent(
    value,
  )}; Max-Age=${maxAgeSeconds}; Path=/; ${sameSite}`;
}

function isConsentState(value: unknown): value is ConsentState {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.v === "number" &&
    typeof v.ts === "number" &&
    v.necessary === true &&
    typeof v.preferences === "boolean" &&
    typeof v.analytics === "boolean" &&
    typeof v.marketing === "boolean"
  );
}

export function getConsent(): ConsentState | null {
  const raw = readRawCookie(COOKIE_NAME);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isConsentState(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function hasValidConsent(): boolean {
  const state = getConsent();
  return state !== null && state.v === COOKIE_VERSION;
}

export function setConsent(input: ConsentInput): ConsentState {
  const state: ConsentState = {
    v: COOKIE_VERSION,
    ts: Math.floor(Date.now() / 1000),
    necessary: true,
    preferences: input.preferences === true,
    analytics: input.analytics === true,
    marketing: input.marketing === true,
  };
  writeRawCookie(COOKIE_NAME, JSON.stringify(state), COOKIE_MAX_AGE_DAYS * ONE_DAY_SECONDS);
  return state;
}

export function acceptAll(): ConsentState {
  return setConsent({ preferences: true, analytics: true, marketing: true });
}

export function rejectAll(): ConsentState {
  return setConsent({ preferences: false, analytics: false, marketing: false });
}

// --- Analytics gating -------------------------------------------------------
// Shared by every analytics vendor wrapper (GA, HubSpot, ...).
//
// Opt-out model: a visitor with no stored choice is treated as accepting, so
// trackers run while the banner is still on screen. An explicit choice always
// wins over that default — rejecting stops the trackers and revokes their
// cookies. Note this is implied consent, which GDPR Art. 4(11) / Recital 32 and
// EDPB Guidelines 05/2020 do not recognise for the EU; flip IMPLIED_CONSENT to
// false to go back to load-only-after-acceptance.
const IMPLIED_CONSENT = true;

// null = "no decision and no implied default" — a state trackers must not load
// in. Reachable only when IMPLIED_CONSENT is false.
export type AnalyticsConsent = boolean | null;

export type ConsentAction =
  | "none" // nothing to do
  | "load" // inject the tag for the first time
  | "stop" // withdrawn: block tracking + drop the vendor's cookies
  | "resume"; // re-accepted: lift the previous block

export function readAnalyticsConsent(): AnalyticsConsent {
  const state = getConsent();
  if (state?.v === COOKIE_VERSION) return state.analytics;
  // No cookie yet, or a stale version that re-prompts.
  return IMPLIED_CONSENT ? true : null;
}

/** `started` = the tag has already been injected during this page's lifetime. */
export function consentAction(analytics: AnalyticsConsent, started: boolean): ConsentAction {
  if (analytics === null) return "none";
  if (analytics) return started ? "resume" : "load";
  return started ? "stop" : "none";
}

// Event name used to reopen the preferences panel from any client component
// (e.g. the footer "Cookie preferences" link).
export const OPEN_PREFERENCES_EVENT = "niftipay:open-cookie-preferences";

// Dispatched by <CookieConsentProvider> on every save (accept all, reject all,
// save preferences, dismiss). Analytics gates subscribe to it for live updates.
export const CONSENT_CHANGED_EVENT = "niftipay:consent-changed";

export function dispatchOpenPreferences(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_PREFERENCES_EVENT));
}
